import { useState } from 'react'
import { formatDay } from '../../lib/format'
import { matches } from '../../lib/database'
import type { VenueEntry } from '../../lib/database'
import type { Contact, Hotel, WithId } from '../../types/models'
import Icon from '../../components/Icon'
import Empty from '../../components/Empty'

/* ============================================================================
   The database lists.

   Shared by the desktop and phone screens — the rows are the same rows, only
   the chrome around them differs. Building two of these is how they end up
   disagreeing about what a contact shows.
   ============================================================================ */

export type Section = 'contacts' | 'venues' | 'hotels'

export const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'contacts', label: 'Contacts' },
  { id: 'venues', label: 'Venues' },
  { id: 'hotels', label: 'Hotels' },
]

/* ---------------------------------------------------------------- contacts */

export function ContactList({
  contacts,
  query,
  role,
  loading,
  canEdit,
  onEdit,
  onAdd,
}: {
  contacts: WithId<Contact>[]
  query: string
  role: string | null
  loading: boolean
  canEdit: boolean
  onEdit: (contact: WithId<Contact>) => void
  onAdd: () => void
}) {
  const [openId, setOpenId] = useState<string | null>(null)

  const visible = contacts.filter((contact) => {
    if (role && !(contact.roles ?? []).includes(role)) return false
    return matches(query, contact.name, contact.email, contact.phone, contact.location, ...(contact.roles ?? []))
  })

  if (!loading && contacts.length === 0) {
    return (
      <Empty
        title="No contacts yet"
        message="Promoters, engineers, drivers, venue managers — everyone you'd otherwise dig out of an old email."
        action={canEdit ? { label: 'Add a contact', onClick: onAdd } : undefined}
      />
    )
  }

  if (visible.length === 0) {
    return <p className="panel panel-pad t-caption">Nobody matches that.</p>
  }

  return (
    <div className="panel rows">
      {visible.map((contact) => {
        const open = openId === contact.id
        const expiring = passportExpiry(contact)

        return (
          <div key={contact.id}>
            <button
              onClick={() => setOpenId(open ? null : contact.id)}
              aria-expanded={open}
              className="row"
              style={{ width: '100%', padding: '14px 16px', gap: 13, textAlign: 'left' }}
            >
              <Initials name={contact.name} />
              <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 600 }}>
                  {contact.name}
                </span>
                <span className="t-caption truncate">
                  {contact.roles?.join(' · ') || contact.location || '—'}
                </span>
              </span>
              {expiring && <span className="chip chip-warning">Passport {expiring}</span>}
              <Icon
                name={open ? 'chevronDown' : 'chevronRight'}
                size={15}
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>

            {open && (
              <div
                className="stack"
                style={{ gap: 14, padding: 16, background: 'var(--panel-raised)' }}
              >
                <div
                  className="responsive-grid"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
                >
                  <Detail
                    label="Email"
                    value={
                      contact.email ? (
                        <a className="accent" href={`mailto:${contact.email}`}>
                          {contact.email}
                        </a>
                      ) : null
                    }
                  />
                  <Detail
                    label="Phone"
                    value={
                      contact.phone ? (
                        <a className="accent" href={`tel:${contact.phone}`}>
                          {contact.phone}
                        </a>
                      ) : null
                    }
                  />
                  <Detail label="Based in" value={contact.location} />
                  <Detail
                    label="Emergency contact"
                    value={
                      contact.emergencyContact
                        ? `${contact.emergencyContact.name} · ${contact.emergencyContact.phone}`
                        : null
                    }
                  />
                  <Detail
                    label="Passport"
                    value={
                      contact.passport
                        ? `${contact.passport.passportNumber} · ${contact.passport.issuingCountry} · expires ${formatDay(contact.passport.expiryDate)}`
                        : null
                    }
                  />
                  <Detail label="Allergies" value={contact.allergies} />
                  <Detail label="Medications" value={contact.medications} />
                </div>

                {contact.loyaltyPrograms && contact.loyaltyPrograms.length > 0 && (
                  <div className="stack" style={{ gap: 4 }}>
                    <span className="t-eyebrow">Frequent flyer</span>
                    {contact.loyaltyPrograms.map((program) => (
                      <div key={program.id} className="spread">
                        <span className="t-caption">{program.airline}</span>
                        <span className="t-spec-caption dim">{program.accountNumber}</span>
                      </div>
                    ))}
                  </div>
                )}

                {contact.notes && <Detail label="Notes" value={contact.notes} />}

                {canEdit && (
                  <button
                    className="btn btn-secondary"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => onEdit(contact)}
                  >
                    <Icon name="edit" size={15} /> Edit contact
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * "in 4 months" when a passport expires inside six.
 *
 * Most countries refuse entry inside six months of expiry, so this is a real
 * operational warning, not a nicety — it's the thing that strands someone at
 * check-in.
 */
function passportExpiry(contact: WithId<Contact>): string | null {
  if (!contact.passport) return null
  const expiry = contact.passport.expiryDate.toDate()
  const months = (expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.4)
  if (months > 6) return null
  if (months <= 0) return 'expired'
  return `${Math.max(1, Math.round(months))}mo`
}

/* ------------------------------------------------------------------ venues */

export function VenueList({
  venues,
  query,
  city,
  loading,
  canEdit,
  onEdit,
  onAdd,
}: {
  venues: VenueEntry[]
  query: string
  city: string | null
  loading: boolean
  canEdit: boolean
  onEdit: (venue: VenueEntry) => void
  onAdd: () => void
}) {
  const [openId, setOpenId] = useState<string | null>(null)

  const visible = venues.filter((venue) => {
    if (city && venue.city !== city) return false
    return matches(query, venue.name, venue.city, venue.address, venue.contactName)
  })

  if (!loading && venues.length === 0) {
    return (
      <Empty
        title="No venues yet"
        message="Venues appear here as you book shows, and you can save your own."
        action={canEdit ? { label: 'Add a venue', onClick: onAdd } : undefined}
      />
    )
  }

  if (visible.length === 0) {
    return <p className="panel panel-pad t-caption">No venues match that.</p>
  }

  return (
    <div className="panel rows">
      {visible.map((venue) => {
        const open = openId === venue.id
        return (
          <div key={venue.id}>
            <button
              onClick={() => setOpenId(open ? null : venue.id)}
              aria-expanded={open}
              className="row"
              style={{ width: '100%', padding: '14px 16px', gap: 13, textAlign: 'left' }}
            >
              <Icon name="mapPin" size={18} style={{ color: 'var(--text-tertiary)' }} />
              <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 600 }}>
                  {venue.name}
                </span>
                <span className="t-caption truncate">
                  {[venue.city, venue.country].filter(Boolean).join(', ')}
                </span>
              </span>
              {venue.playCount > 0 && (
                <span className="chip">
                  {venue.playCount} {venue.playCount === 1 ? 'show' : 'shows'}
                </span>
              )}
              {!venue.saved && <span className="chip chip-warning">Unsaved</span>}
            </button>

            {open && (
              <div
                className="stack"
                style={{ gap: 14, padding: 16, background: 'var(--panel-raised)' }}
              >
                <div
                  className="responsive-grid"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
                >
                  <Detail label="Address" value={venue.address} />
                  <Detail
                    label="Capacity"
                    value={venue.capacity ? String(venue.capacity) : null}
                  />
                  <Detail label="Contact" value={venue.contactName} />
                  <Detail
                    label="Email"
                    value={
                      venue.contactEmail ? (
                        <a className="accent" href={`mailto:${venue.contactEmail}`}>
                          {venue.contactEmail}
                        </a>
                      ) : null
                    }
                  />
                  <Detail
                    label="Phone"
                    value={
                      venue.contactPhone ? (
                        <a className="accent" href={`tel:${venue.contactPhone}`}>
                          {venue.contactPhone}
                        </a>
                      ) : null
                    }
                  />
                  <Detail
                    label="Last played"
                    value={venue.lastPlayed ? formatDay(venue.lastPlayed) : 'Never'}
                  />
                </div>

                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {venue.address && (
                    <a
                      className="btn btn-secondary"
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        [venue.name, venue.address, venue.city].filter(Boolean).join(', '),
                      )}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <Icon name="mapPin" size={15} /> Open in Maps
                    </a>
                  )}
                  {canEdit && (
                    <button className="btn btn-secondary" onClick={() => onEdit(venue)}>
                      <Icon name={venue.saved ? 'edit' : 'plus'} size={15} />
                      {venue.saved ? 'Edit venue' : 'Save to database'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ hotels */

export function HotelList({
  hotels,
  query,
  loading,
}: {
  hotels: WithId<Hotel>[]
  query: string
  loading: boolean
}) {
  const visible = hotels.filter((hotel) =>
    matches(query, hotel.name, hotel.city, hotel.address, hotel.country),
  )

  if (!loading && hotels.length === 0) {
    return (
      <Empty
        title="No hotels yet"
        message="Hotels you add to a tour show up here, so you can find the one you liked in Berlin."
      />
    )
  }

  if (visible.length === 0) {
    return <p className="panel panel-pad t-caption">No hotels match that.</p>
  }

  return (
    <div className="panel rows">
      {visible.map((hotel) => (
        <div key={hotel.id} className="row" style={{ padding: '14px 16px', gap: 13 }}>
          <Icon name="bed" size={18} style={{ color: 'var(--text-tertiary)' }} />
          <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
            <span className="truncate" style={{ fontWeight: 600 }}>
              {hotel.name}
            </span>
            <span className="t-caption truncate">
              {[hotel.city, hotel.country].filter(Boolean).join(', ')}
            </span>
          </span>
          <span className="t-spec-caption faint" style={{ whiteSpace: 'nowrap' }}>
            {formatDay(hotel.checkInDate, hotel.timezone)}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ----------------------------------------------------------------- pieces */

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null
  return (
    <div className="stack" style={{ gap: 1, minWidth: 0 }}>
      <span className="t-eyebrow">{label}</span>
      <span className="t-body" style={{ wordBreak: 'break-word' }}>
        {value}
      </span>
    </div>
  )
}

export function Initials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 36,
        height: 36,
        flex: 'none',
        borderRadius: '50%',
        background: 'var(--accent-wash)',
        color: 'var(--accent-text)',
        fontWeight: 700,
        fontSize: '0.76rem',
      }}
    >
      {initials || '—'}
    </span>
  )
}
